import { useMemo, useRef, useState, useEffect } from 'preact/hooks';
import { services } from '../app';
import { observingNight } from '../astro/solar';
import { goLive, panel, place, setRate, setSimTime, simNow, timeLive, timeRate, timeVersion } from '../state';
import { fmtHijri } from '../astro/hijri';
import { hijriNow } from '../live/saudi';
import { Sheet, shareSky } from './chrome';
import { fmtDate, fmtRelative, fmtTime, fromPlaceInput, toPlaceInput } from './format';
import { Icon } from './icons';

const RATES: [number, string][] = [[-3600, '−1 h/s'], [-60, '−1 min/s'], [0, 'Pause'], [1, 'Real time'], [60, '1 min/s'], [600, '10 min/s'], [3600, '1 h/s']];

export function TimePanel() {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((x) => x + 1), 250);
    return () => clearInterval(id);
  }, []);
  void timeVersion.value;
  const base = useRef(simNow().getTime());
  const [slider, setSlider] = useState(0);
  const [special, setSpecial] = useState('');
  const now = simNow();
  const rate = timeRate.value;
  const s = services.value!;
  const obs = s.engine.frame.observer;

  const minute = Math.floor(now.getTime() / 60e3);
  const jumps = useMemo(() => {
    // The night being shown, as Tonight has it: the one in progress until sunrise, then the coming one.
    const night = observingNight(obs, new Date(minute * 60e3));
    const out: [string, Date][] = [];
    if (night.sunset) out.push(['Sunset', night.sunset]);
    // Dark as Tonight has it: full darkness, or the darkest the summer sky gets where it never comes.
    const dark = night.astroDusk ?? night.nauticalDusk;
    if (dark) out.push(['Dark', dark]);
    // Solar midnight: the Sun at its lowest, the middle of the night (not 00:00 on the clock).
    out.push(['Middle of the night', new Date(night.noon.getTime() + 12 * 3600e3)]);
    if (night.sunset && night.civilDawn) out.push(['Dawn', night.civilDawn]);
    return out;
  }, [obs.latitude, obs.longitude, minute]);

  const onSlide = (v: number) => {
    setSlider(v);
    setSimTime(new Date(base.current + v * 60e3), 0);
  };
  const shift = (days: number) => {
    base.current = simNow().getTime() + days * 86400e3;
    setSlider(0);
    setSimTime(new Date(base.current), rate === 1 ? 0 : rate);
  };
  return (
    <Sheet onClose={() => (panel.value = null)} label="Time">
      <h2 class="sr-only">Time</h2>
      <div class="time-big">
        <div class="time-date">{fmtDate(now, true)}</div>
        <div class="time-hijri">{fmtHijri(hijriNow(now.getTime(), place.value))}</div>
        <div class="time-clock tnum">{fmtTime(now, true)}</div>
        <div class={`time-rel ${timeLive.value ? 'live' : ''}`}>{timeLive.value ? 'Live' : fmtRelative(now.getTime())}</div>
      </div>
      <div class="slider-wrap">
        <input type="range" min={-720} max={720} step={1} value={slider} onInput={(e) => onSlide(Number((e.target as HTMLInputElement).value))} aria-label="Move through time"
          aria-valuetext={slider === 0 ? 'No change' : `${Math.abs(slider) >= 60 ? `${Math.floor(Math.abs(slider) / 60)} h ${Math.abs(slider) % 60} min` : `${Math.abs(slider)} min`} ${slider > 0 ? 'later' : 'earlier'}, ${fmtTime(now)}`} />
        <div class="slider-scale"><span>−12 h</span><span>0</span><span>+12 h</span></div>
      </div>
      <div class="chips-row">
        {jumps.map(([label, d]) => (
          <button class="pill" key={label} onClick={() => { base.current = d.getTime(); setSlider(0); setSimTime(d, 0); }}>
            {label} <span class="muted tnum">{fmtTime(d)}</span>
          </button>
        ))}
      </div>
      <div class="chips-row">
        <button class="pill" onClick={() => shift(-1)}><Icon name="back" size={14} /> Day</button>
        <button class="pill" onClick={() => shift(1)}>Day <Icon name="forward" size={14} /></button>
        <label class="pill pill-input">
          <Icon name="calendar" size={14} />
          <input type="datetime-local" value={toPlaceInput(now)} aria-label="Date and time at this place" onChange={(e) => {
            const d = fromPlaceInput((e.target as HTMLInputElement).value);
            if (d) { base.current = d.getTime(); setSlider(0); setSimTime(d, 0); }
          }} />
        </label>
      </div>
      <div class="special-date">
        <label class="row-label" for="special-date">The sky on a special day</label>
        <div class="special-row">
          <input id="special-date" type="date" min="1600-01-01" max="2200-12-31" value={special} onInput={(e) => setSpecial((e.target as HTMLInputElement).value)} />
          <button class="btn small" disabled={!special} onClick={() => {
            // 9 pm local time on that day.
            const d = fromPlaceInput(`${special}T21:00`);
            if (d) { base.current = d.getTime(); setSlider(0); setSimTime(d, 0); }
          }}>Show</button>
        </div>
        <div class="row-sub">Any date you wish, then share it.</div>
      </div>
      <div class="row-label">Speed</div>
      <div class="row-sub speed-note">How much sky time passes each second: watch stars rise and set, or pause the sky.</div>
      <div class="rates">
        {RATES.map(([r, l]) => (
          <button key={r} class={`rate ${rate === r ? 'on' : ''}`} onClick={() => setRate(r)}>{l}</button>
        ))}
      </div>
      <div class={`time-actions ${timeLive.value ? 'single' : ''}`}>
        <button class="btn-ghost" onClick={() => void shareSky()} aria-label="Share this view"><Icon name="share" size={17} /> Share</button>
        {!timeLive.value && <button class="btn" onClick={() => { goLive(); base.current = Date.now(); setSlider(0); }}>Back to now</button>}
      </div>
    </Sheet>
  );
}
